import { DungeonEntity } from './DungeonEntity';
import { DungeonEntityProps, AIArchetype } from '../types/entity.types';
import { ActionDefinition, MonsterAIState } from '../types/types';
import { checkCondition } from '../effect-system/conditionChecker';
import {
  AIBehaviorState,
  DirectionEnum,
  DungeonEntityType,
  Element,
  LagType,
} from '../types/enums';
import { getTargetPosition } from '../utils/map.utils';
import { msToTicks } from '../utils/timing.utils';
import { dungeonService } from '../services/DungeonService';
import { mapService } from '../services/MapService';
import { aiService } from '../services/AIService';
import { PlayerEntity } from './PlayerEntity';
import {
  DEFAULT_AI_ACTION_INTERVAL_MS,
  DEFAULT_ELITE_SCALING,
  EliteScaling,
  MonsterMapIcon,
  MONSTER_CONFIGS,
  spellEntryId,
  spellEntryModifiers,
} from '../data/monsters.config';
import { chebyshevDistance } from '../utils/distance.utils';
import { BUFF_REGISTRY } from '../data/buffs.config';
import { ActiveBuff } from '../types/buff.types';
import { useCharacterProgressionStore } from '../store/characterProgressionSlice';
import { onBossFirstKill } from '../services/ContentUnlockService';
import { spellExecutor } from '../services/SpellService';
import { initializeBuffDefinition } from '../utils/buff.utils';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { EffectContext } from '../types/spell.types';
import { audioService } from '../services/AudioService';
import { SPELL_REGISTRY } from '../data/spells.config';
import { ActionPhase } from '../types/enums';
import { netService } from '../services/NetService';
import { makeMonsterKilled } from '../net/sessionEvents';

export class MonsterEntity extends DungeonEntity {
  public aiEnabled = true;
  /** When true, the monster never enables its AI — not even on damage (test dummies). */
  public aiLocked = false;
  public dormantRoomIndex = -1;
  public aiState: MonsterAIState = {
    behaviorState: AIBehaviorState.IDLE,
    lastDecisionTick: 0,
    targetEntityId: null,
    aggroLevel: 0,
    visionRange: 8,
    actionCooldownTicks: 0,
    committedSpellId: null,
  };
  private patrolIndex = 0;
  private aiDecisionCounter = 0;

  /** Last activated phase index, to trigger onEnter only once. */
  private activePhaseIndex = -1;

  /** Damage accumulated during current cast (for interruptible spells). */
  public damageDuringCast = 0;

  /**
   * On death, a floor boss spawns a descending staircase that lets the player
   * progress to the next floor — or ends the run on the last floor.
   */
  public isFloorBoss = false;

  /**
   * Promotion (cf. `makeElite`) triggers `eliteLoot` drops, a visual buff and
   * stat scaling (`EliteScaling`).
   */
  public isElite = false;

  /** Marker kind drawn on the map/minimap. Default 'normal'; raised by
   *  makeElite / lair / boss spawn. */
  public mapIcon: MonsterMapIcon = 'normal';

  constructor(props: Omit<DungeonEntityProps, 'type'>) {
    super({ ...props, type: DungeonEntityType.MONSTER });
  }

  public tick(deltaTime: number): void {
    super.tick(deltaTime);
    if (this.aiState.actionCooldownTicks > 0) {
      this.aiState.actionCooldownTicks -= deltaTime;
    }
  }

  protected computeEmotionTypes(): Element[] {
    return MONSTER_CONFIGS[this.configId]?.emotionTypes ?? [];
  }

  /** Idempotent: a repeated call is a no-op. */
  public makeElite(scaling: EliteScaling = DEFAULT_ELITE_SCALING): void {
    if (this.isElite) {
      return;
    }
    this.isElite = true;
    if (this.mapIcon === 'normal') {
      this.mapIcon = 'elite';
    }
    const m = scaling.statMultipliers ?? {};
    if (m.life) {
      this.maxLife = Math.round(this.maxLife * m.life);
      this.life = this.maxLife;
      this.baseStats = { ...this.baseStats, maxLife: this.maxLife };
    }
    const scalarKeys = [
      'physicalAttack',
      'physicalDefense',
      'magicAttack',
      'magicDefense',
      'moveSpeed',
    ] as const;
    const nextBase = { ...this.baseStats };
    for (const key of scalarKeys) {
      const mult = m[key];
      if (mult !== undefined) {
        nextBase[key] = Math.round(nextBase[key] * mult);
      }
    }
    this.baseStats = nextBase;
    const buffDef = BUFF_REGISTRY[scaling.visualBuffId ?? 'elite-aura'];
    if (buffDef) {
      const buff: ActiveBuff = { def: buffDef, source: 'elite' };
      this.addBuff(buff);
    }
    this.recomputeStats();
    this.markDirty();
  }

  public getActivePhaseIndex(): number {
    return this.activePhaseIndex;
  }

  public restorePhaseIndex(idx: number): void {
    this.activePhaseIndex = idx;
  }

  getSpellIds(): string[] {
    const cfg = MONSTER_CONFIGS[this.configId];
    if (!cfg) {
      return [];
    }
    const ids = (cfg.spellIds || []).map(spellEntryId);

    if (cfg.phases) {
      const hpRatio = this.life / this.maxLife;
      let phaseIdx = -1;
      for (let i = cfg.phases.length - 1; i >= 0; i--) {
        if (hpRatio <= cfg.phases[i].hpThreshold) {
          phaseIdx = i;
          break;
        }
      }
      if (phaseIdx >= 0) {
        ids.push(...cfg.phases[phaseIdx].spellIds);
        if (phaseIdx !== this.activePhaseIndex) {
          this.activePhaseIndex = phaseIdx;
          const onEnter = cfg.phases[phaseIdx].onEnter;
          if (onEnter) {
            const ctx: EffectContext = {
              caster: this,
              direction: this.direction,
              affectedCells: [],
            };
            for (const step of onEnter) {
              EFFECT_HANDLERS[step.handler]?.(ctx, step.params);
            }
          }
        }
      }
    }
    return ids;
  }

  private spellModifierFor(spellId: string): {
    cooldownModifier: number;
    castTimeModifier: number;
  } {
    const entries = MONSTER_CONFIGS[this.configId]?.spellIds ?? [];
    for (const e of entries) {
      if (spellEntryId(e) === spellId) {
        return spellEntryModifiers(e);
      }
    }
    return { cooldownModifier: 100, castTimeModifier: 100 };
  }

  public override getSpellCooldownMult(spellId: string): number {
    const mod = this.spellModifierFor(spellId);
    return Math.max(
      0,
      this.stats.spellCooldown + (mod.cooldownModifier - 100) / 100,
    );
  }

  public override getSpellCastTimeMult(spellId: string): number {
    const mod = this.spellModifierFor(spellId);
    return Math.max(
      0.05,
      this.stats.spellCastTime + (mod.castTimeModifier - 100) / 100,
    );
  }

  getAvailableActions(): ActionDefinition[] {
    return [
      {
        name: 'move',
        parameter: { type: 'position' },
        execute: (x: number, y: number) => this.move(x, y),
      },
      {
        name: 'face',
        parameter: { type: 'direction' },
        execute: (direction: DirectionEnum) => this.face(direction),
      },
      ...this.getSpellIds()
        .map((id) => spellExecutor.toActionDefinition(this, id))
        .filter((a): a is ActionDefinition => a !== null),
    ];
  }

  public initializeBuffs(): void {
    const cfg = MONSTER_CONFIGS[this.configId];
    if (!cfg?.initialBuffs) {
      return;
    }
    for (const buffId of cfg.initialBuffs) {
      const buff = initializeBuffDefinition(buffId);
      if (buff) {
        this.addBuff(buff);
      }
    }
  }

  takeDamage(damage: number, crit = false, element?: Element): number {
    const action = this.getActionState();
    if (action) {
      this.damageDuringCast += damage;
      const def = SPELL_REGISTRY[action.name];
      if (
        def?.interruptible &&
        action.phase === ActionPhase.CASTING &&
        this.damageDuringCast >= def.interruptible.damageThreshold
      ) {
        const ctx: EffectContext = {
          caster: this,
          direction: this.direction,
          affectedCells: [],
          spell: def,
        };
        for (const step of def.interruptible.onInterrupt) {
          EFFECT_HANDLERS[step.handler]?.(ctx, { ...(step.params ?? {}) });
        }
        this.cancelCast();
        this.damageDuringCast = 0;
      }
    }

    if (!this.aiEnabled && !this.aiLocked) {
      this.aiEnabled = true;
      this.dormantRoomIndex = -1;
    }

    const dealt = super.takeDamage(damage, crit, element);
    if (dealt > 0) {
      const sfx = MONSTER_CONFIGS[this.configId]?.onHitSfx;
      if (sfx) {
        audioService.playSfx(sfx);
      }
    }
    return dealt;
  }

  toggleAI(): void {
    this.aiEnabled = !this.aiEnabled;
  }

  executeAI(): void {
    if (!this.aiEnabled || !this.canCast()) {
      return;
    }

    if (this.getCurrentBlocking() === LagType.FULL) {
      return;
    }

    this.aiDecisionCounter++;

    const decisionCooldown = 1;
    if (this.aiDecisionCounter < decisionCooldown) {
      return;
    }

    this.aiDecisionCounter = 0;

    const player = aiService.detectPlayer(this, this.aiState.visionRange || 8);

    if (player) {
      this.aiState.lastKnownPlayerPosition = {
        x: player.positionX,
        y: player.positionY,
      };
      this.aiState.targetEntityId = player.id;
    }

    // Aggro reset (Voile-Doux minor trinket): when the player carries the
    // aggro-reset buff and their opacity is low enough, the monster "loses
    // sight" after 3000 ms (180 ticks at 60 FPS) of effective invisibility.
    const playerForAggro = dungeonService.getPlayer();
    if (playerForAggro && this.aiState.targetEntityId === playerForAggro.id) {
      const hasAggroResetBuff = playerForAggro
        .getActiveBuffs()
        .some((b) => b.def.id === 'aggro-reset');
      const opacityLow = playerForAggro.stats.opacity < 0.5;
      if (hasAggroResetBuff && opacityLow) {
        this.aiState.aggroDecayTicks = (this.aiState.aggroDecayTicks ?? 0) + 1;
        if (this.aiState.aggroDecayTicks >= 180) {
          this.aiState.behaviorState = AIBehaviorState.IDLE;
          this.aiState.targetEntityId = null;
          this.aiState.lastKnownPlayerPosition = undefined;
          this.aiState.aggroDecayTicks = undefined;
        }
      } else {
        this.aiState.aggroDecayTicks = undefined;
      }
    } else {
      this.aiState.aggroDecayTicks = undefined;
    }

    const cfg = MONSTER_CONFIGS[this.configId];
    const hasFearBuff = this.getActiveBuffs().some((b) => b.def.id === 'fear');
    const belowHpThreshold =
      cfg?.fleeBelowHpPercent !== undefined &&
      this.life / this.maxLife < cfg.fleeBelowHpPercent;

    if (hasFearBuff || belowHpThreshold) {
      this.aiState.behaviorState = AIBehaviorState.FLEE;
      this.executeFleeBehavior(player);
      return;
    }

    const hasLastKnownPosition = !!this.aiState.lastKnownPlayerPosition;
    const hasPatrolPath = !!(
      this.aiState.patrolPath && this.aiState.patrolPath.length > 0
    );
    this.aiState.behaviorState = aiService.determineBehaviorState(
      !!player,
      hasLastKnownPosition,
      hasPatrolPath,
    );

    switch (this.aiState.behaviorState) {
      case AIBehaviorState.COMBAT:
        this.executeCombatBehavior(player as PlayerEntity);
        break;

      case AIBehaviorState.CHASE:
        this.executeChaseBehavior();
        break;

      case AIBehaviorState.PATROL:
        this.executePatrolBehavior();
        break;

      case AIBehaviorState.FLEE:
      case AIBehaviorState.IDLE:
      default:
        this.executeIdleBehavior();
        break;
    }
  }

  private executeCombatBehavior(player: DungeonEntity): void {
    const cadenceReady = this.aiState.actionCooldownTicks <= 0;
    const ready = this.getReadySpellActions();

    if (cadenceReady) {
      const castable = ready.filter((a) =>
        aiService.canCastNow(this, a, player),
      );
      if (castable.length > 0) {
        const chosen = this.pickCastNow(castable);
        if (chosen.parameter?.type === 'self') {
          chosen.execute();
        } else {
          chosen.execute(player.positionX, player.positionY);
        }
        this.aiState.committedSpellId = null;
        this.rechargeActionCadence();
        return;
      }

      const committed = this.pickCommittedSpell(ready);
      if (committed) {
        this.aiState.committedSpellId = committed.name;
        this.positionForSpell(committed, player);
        return;
      }

      this.aiState.committedSpellId = null;
      this.executeArchetypeFallback(player);
      return;
    }

    const committedAction = this.aiState.committedSpellId
      ? ready.find((a) => a.name === this.aiState.committedSpellId)
      : undefined;
    if (committedAction) {
      this.positionForSpell(committedAction, player);
    } else {
      this.maintainArchetypeDistance(player);
    }
  }

  private getArchetype(): AIArchetype {
    return MONSTER_CONFIGS[this.configId]?.aiArchetype ?? 'melee';
  }

  private rechargeActionCadence(): void {
    const intervalMs =
      MONSTER_CONFIGS[this.configId]?.actionIntervalMs ??
      DEFAULT_AI_ACTION_INTERVAL_MS;
    this.aiState.actionCooldownTicks = msToTicks(intervalMs);
  }

  private getReadySpellActions(): ActionDefinition[] {
    return this.getAvailableActions().filter((a) => {
      if (a.name === 'move' || a.name === 'face') {
        return false;
      }
      if (this.getSpellState(a.name).cooldownRemaining > 0) {
        return false;
      }
      const def = SPELL_REGISTRY[a.name];
      if (def?.castCondition && !checkCondition(this, def.castCondition)) {
        return false;
      }
      return true;
    });
  }

  private isSelfBuff(a: ActionDefinition): boolean {
    return a.parameter?.type === 'self' && (!a.shapes || a.shapes.length === 0);
  }

  private pickByArchetype(actions: ActionDefinition[]): ActionDefinition {
    const rangeOf = (a: ActionDefinition) => a.range ?? 1;
    let pool: ActionDefinition[];
    if (this.getArchetype() === 'ranged') {
      const max = Math.max(...actions.map(rangeOf));
      pool = actions.filter((a) => rangeOf(a) === max);
    } else {
      const min = Math.min(...actions.map(rangeOf));
      pool = actions.filter((a) => rangeOf(a) === min);
    }
    return pool[Math.floor(Math.random() * pool.length)];
  }

  private pickCastNow(castable: ActionDefinition[]): ActionDefinition {
    const buff = castable.find((a) => this.isSelfBuff(a));
    return buff ?? this.pickByArchetype(castable);
  }

  private pickCommittedSpell(
    ready: ActionDefinition[],
  ): ActionDefinition | null {
    const offensive = ready.filter((a) => !this.isSelfBuff(a));
    if (offensive.length === 0) {
      return null;
    }
    return this.pickByArchetype(offensive);
  }

  private approach(player: DungeonEntity): void {
    const moveTarget = aiService.getMovementTowardsTarget(
      this.positionX,
      this.positionY,
      player.positionX,
      player.positionY,
      false,
      true,
    );
    if (moveTarget !== null) {
      this.move(moveTarget.x, moveTarget.y);
    }
  }

  /** Move to satisfy the committed spell's range (approach if too far / no LOS;
   * back off if inside minRange). Self-AOE: approach until the target is in the
   * zone. In range → hold. */
  private positionForSpell(
    action: ActionDefinition,
    player: DungeonEntity,
  ): void {
    if (action.parameter?.type === 'self') {
      if (!aiService.canCastNow(this, action, player)) {
        this.approach(player);
      }
      return;
    }
    const dist = chebyshevDistance(
      this.positionX,
      this.positionY,
      player.positionX,
      player.positionY,
    );
    const range = action.range ?? 1;
    const minRange = action.minRange ?? 0;
    const losOk = aiService.hasLineOfSight(
      this.positionX,
      this.positionY,
      player.positionX,
      player.positionY,
    );
    if (dist > range || !losOk) {
      this.approach(player);
    } else if (dist < minRange) {
      this.stepAwayFrom(player.positionX, player.positionY);
    }
  }

  private getRangedPreferredDistance(): number {
    let max = 0;
    for (const id of this.getSpellIds()) {
      const def = SPELL_REGISTRY[id];
      if (!def || def.parameter.type === 'self') {
        continue;
      }
      max = Math.max(max, def.range ?? 1);
    }
    return max > 1 ? max : 3;
  }

  private maintainArchetypeDistance(player: DungeonEntity): void {
    const arch = this.getArchetype();
    if (arch === 'flee') {
      this.stepAwayFrom(player.positionX, player.positionY);
      return;
    }
    const dist = chebyshevDistance(
      this.positionX,
      this.positionY,
      player.positionX,
      player.positionY,
    );
    if (arch === 'melee') {
      if (dist > 1) {
        this.approach(player);
      }
      return;
    }
    const pref = this.getRangedPreferredDistance();
    if (dist > pref) {
      this.approach(player);
    } else if (dist < pref) {
      this.stepAwayFrom(player.positionX, player.positionY);
    }
  }

  /** No spell available: melee closes + auto-attacks adjacent; ranged kites and
   * (cornered to melee) rolls 33% flee / 66% auto-attack; flee retreats. */
  private executeArchetypeFallback(player: DungeonEntity): void {
    const arch = this.getArchetype();
    if (arch === 'flee') {
      this.stepAwayFrom(player.positionX, player.positionY);
      return;
    }
    const dist = chebyshevDistance(
      this.positionX,
      this.positionY,
      player.positionX,
      player.positionY,
    );
    const base = spellExecutor.toActionDefinition(this, 'basic-strike');
    const baseReady =
      !!base && this.getSpellState('basic-strike').cooldownRemaining <= 0;

    if (arch === 'melee') {
      if (dist <= 1 && base && baseReady) {
        base.execute(player.positionX, player.positionY);
        this.rechargeActionCadence();
      } else {
        this.approach(player);
      }
      return;
    }

    if (dist <= 1) {
      if (Math.random() < 0.33 || !base || !baseReady) {
        this.stepAwayFrom(player.positionX, player.positionY);
      } else {
        base.execute(player.positionX, player.positionY);
        this.rechargeActionCadence();
      }
      return;
    }
    const pref = this.getRangedPreferredDistance();
    if (dist > pref) {
      this.approach(player);
    } else if (dist < pref) {
      this.stepAwayFrom(player.positionX, player.positionY);
    }
  }

  /**
   * Steps to the walkable neighbor that maximizes Chebyshev distance to the
   * target. Returns false (no move) when no neighbor increases the distance.
   */
  private stepAwayFrom(targetX: number, targetY: number): boolean {
    const neighbors = mapService.getNeighbors(this.positionX, this.positionY);
    let bestCell: { x: number; y: number } | null = null;
    let bestDist = chebyshevDistance(
      this.positionX,
      this.positionY,
      targetX,
      targetY,
    );

    for (const n of neighbors) {
      if (
        !mapService.isWalkable(n.x, n.y) ||
        !mapService.isDiagonalValid(this.positionX, this.positionY, n.x, n.y)
      ) {
        continue;
      }
      const d = chebyshevDistance(n.x, n.y, targetX, targetY);
      if (d > bestDist) {
        bestDist = d;
        bestCell = n;
      }
    }

    if (bestCell) {
      this.move(bestCell.x, bestCell.y);
      return true;
    }
    return false;
  }

  private executeChaseBehavior(): void {
    if (!this.aiState.lastKnownPlayerPosition) {
      return;
    }

    const { x, y } = this.aiState.lastKnownPlayerPosition;

    if (this.positionX === x && this.positionY === y) {
      this.aiState.lastKnownPlayerPosition = undefined;
      this.aiState.behaviorState = AIBehaviorState.IDLE;
      return;
    }

    const moveTarget = aiService.getMovementTowardsTarget(
      this.positionX,
      this.positionY,
      x,
      y,
    );

    if (moveTarget !== null) {
      this.move(moveTarget.x, moveTarget.y);
    } else {
      this.aiState.lastKnownPlayerPosition = undefined;
    }
  }

  private executePatrolBehavior(): void {
    if (!this.aiState.patrolPath || this.aiState.patrolPath.length === 0) {
      this.aiState.patrolPath = aiService.generatePatrolPath(
        this.positionX,
        this.positionY,
        3,
        4,
      );
      this.patrolIndex = 0;
      return;
    }

    const currentTarget = this.aiState.patrolPath[this.patrolIndex];

    if (
      this.positionX === currentTarget.x &&
      this.positionY === currentTarget.y
    ) {
      this.patrolIndex =
        (this.patrolIndex + 1) % this.aiState.patrolPath.length;
      return;
    }

    const moveTarget = aiService.getMovementTowardsTarget(
      this.positionX,
      this.positionY,
      currentTarget.x,
      currentTarget.y,
    );

    if (moveTarget !== null) {
      this.move(moveTarget.x, moveTarget.y);
    } else {
      this.patrolIndex =
        (this.patrolIndex + 1) % this.aiState.patrolPath.length;
    }
  }

  death(): void {
    const baseConfig = MONSTER_CONFIGS[this.configId];
    const isMiniBoss = baseConfig?.isMiniBoss ?? false;
    const isBoss = this.isFloorBoss;
    const richDropMultiplier = this.computeDropRicheMultiplier();
    const killerId = this.lastDamageSourceCasterId;

    // For a floor boss, spread drops over adjacent cells so items don't stack
    // under the staircase we're about to place (they'd be unreachable).
    const positions: { x: number; y: number }[] = [];
    if (isBoss) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) {
            continue;
          }
          const ax = this.positionX + dx;
          const ay = this.positionY + dy;
          // ignoreEntities: the boss is dying, it must not block its own neighbors.
          if (mapService.isWalkable(ax, ay, true)) {
            positions.push({ x: ax, y: ay });
          }
        }
      }
    }
    if (positions.length === 0) {
      positions.push({ x: this.positionX, y: this.positionY });
    }

    if (netService.isHost()) {
      netService.session?.broadcast(
        'reliable',
        makeMonsterKilled(
          this.configId,
          this.level,
          positions,
          this.isElite,
          isMiniBoss,
          isBoss,
          killerId,
          richDropMultiplier,
        ),
      );
    }

    // Each browser rolls its OWN private loot. The rich-drop bonus is the
    // killer's alone; in a co-op host whose avatar didn't land the kill, drop it.
    const localMultiplier =
      netService.isHost() && killerId !== dungeonService.localPlayerId
        ? 1
        : richDropMultiplier;
    const firstKill = !useCharacterProgressionStore
      .getState()
      .bossRewardsClaimed.includes(this.configId);
    dungeonService.monsterSpawningService.rollMonsterLootSpread(
      this.configId,
      positions,
      this.isElite,
      localMultiplier,
      isBoss,
      firstKill,
    );

    dungeonService.applyKillProgression(
      this.configId,
      this.level,
      this.isElite,
    );

    // Floor boss: drop a staircase on its cell so the player can leave the
    // room. On the final floor the staircase triggers run completion (cf.
    // `nextFloor` handler in Dungeon.tsx).
    if (isBoss) {
      dungeonService.locationService.addPlacedLocation({
        id: 'stairs-down',
        x: this.positionX,
        y: this.positionY,
        width: 1,
        height: 1,
        placement: 'floor',
        roomIndex: 0,
        sprite: 'stairs',
        interactionType: 'interact',
      });
    }

    // Canonical boss (floor-boss OR mini-boss): its non-spell first-kill
    // rewards unlock at the END of the death animation (cf. onBossFirstKill).
    // The spell reward is the `guaranteedOnFirstKill` grimoire dropped above
    // via the loot table.
    super.death(() => onBossFirstKill(this.configId));
  }

  private computeDropRicheMultiplier(): number {
    if (!this.lastDamageSourceCasterId || !this.lastDamageSourceSpellId) {
      return 1;
    }
    const killer = dungeonService
      .getAllEntities()
      .find((e) => e.id === this.lastDamageSourceCasterId);
    if (!killer) {
      return 1;
    }
    const tracker = killer
      .getActiveBuffs()
      .find(
        (b) =>
          b.def.id === 'mod-rich-drop-tracker' &&
          b.linkedSpellId === this.lastDamageSourceSpellId,
      );
    return tracker ? 1.5 : 1;
  }

  private executeFleeBehavior(player: PlayerEntity | null): void {
    if (!player) {
      this.executeIdleBehavior();
      return;
    }
    // Cornered (no neighbor increases distance) → the monster defends itself.
    if (!this.stepAwayFrom(player.positionX, player.positionY)) {
      this.executeCombatBehavior(player);
    }
  }

  private executeIdleBehavior(): void {
    if (Math.random() < 0.1) {
      const randomDirection = Math.floor(Math.random() * 8) as DirectionEnum;
      const targetPos = getTargetPosition(
        this.positionX,
        this.positionY,
        randomDirection,
      );
      this.move(targetPos.x, targetPos.y);
    }
  }
}
